import { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ChevronRight, ExternalLink, Eye, Paperclip } from "lucide-react";
import DOMPurify from "dompurify";
import { Spinner } from "@/components/ui/Spinner";
import { AttachmentList } from "@/features/orders/components/AttachmentList";
import { useAuthStore } from "@/store/auth";
import { Stars } from "@/features/rfq/components/Stars";
import { useQuery } from "@tanstack/react-query";
import { useContractorOrders, useProjectContractors } from "../hooks";
import { getContractorPreviewTargets } from "../api";
import { EmptyState, TableShell, Th } from "./ui";
import { PlannedAssignmentStrip } from "./PlannedAssignmentStrip";
import { fmtDate } from "../utils";
import type { ContractorOrderRow, PowerProject, ProjectContractorRow } from "../types";

/**
 * Przypisani podwykonawcy — firmy, które pracowały na tym przedsięwzięciu.
 * Lista jest zbierana serwerowo z zamówień przypisanych do projektu
 * (bezpośrednich, z zapotrzebowania oraz rozliczanych grupowo).
 *
 * Każdy wiersz można rozwinąć, żeby zobaczyć zamówienia danej firmy z danymi
 * zapotrzebowania (read-only, dwie kolumny) i załącznikami — pobierane leniwie.
 */
export function ContractorsTab({ connection }: { connection: PowerProject }) {
  const user = useAuthStore((s) => s.user);
  // Karta podwykonawcy (/contractors/:id) jest dostępna tylko dla dyrektora/koordynatora —
  // dla pozostałych ról pokazujemy nazwę bez linku, żeby nie prowadzić do bramki uprawnień.
  const canOpenContractor =
    !!user && (user.is_ceo || user.role === "director" || user.role === "coordinator");
  // Widok zamówienia (/orders/:id) jest za TechnicalDirectorGuard — odpada tylko dyrektor
  // nietechniczny; podwykonawcy nie mają wstępu na power-projects (DLCModuleGuard).
  const canOpenOrder =
    !!user && !(user.role === "director" && !user.is_technical_director && !user.is_ceo);
  const { data: contractors, isLoading } = useProjectContractors(connection.id);
  // Firmy z zawartością na mapie podwykonawcy (żywe WPW/MDCP albo „planowane") —
  // tylko dla nich pokazujemy wejście „Oczami podwykonawcy".
  const { data: previewTargets } = useQuery({
    queryKey: ["pp", "contractor-preview-targets", connection.id],
    queryFn: () => getContractorPreviewTargets(connection.id),
  });
  const previewIds = new Set(
    (previewTargets?.targets ?? []).map((t) => t.contractor_id),
  );
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const toggle = (id: string) =>
    setExpanded((s) => {
      const next = new Set(s);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  if (isLoading)
    return (
      <div className="py-10 flex justify-center">
        <Spinner />
      </div>
    );

  if (!contractors || contractors.length === 0)
    return (
      <div className="space-y-3">
        {/* „Planowane do zlecenia" żyje TYLKO tutaj (Zespół → Podwykonawcy) — zapowiedź
            ma sens właśnie na świeżym projekcie bez zamówień. */}
        <PlannedAssignmentStrip connectionId={connection.id} />
        <EmptyState>Żaden podwykonawca nie pracował jeszcze na tym przedsięwzięciu.</EmptyState>
      </div>
    );

  return (
    <div className="space-y-3">
      <PlannedAssignmentStrip connectionId={connection.id} />
      <p className="section-label">
        Podwykonawcy z zamówień tego przedsięwzięcia ({contractors.length})
      </p>
      <TableShell>
        <thead>
          <tr>
            <Th className="w-8" />
            <Th>Podwykonawca</Th>
            <Th className="text-right">Zamówienia</Th>
            <Th className="text-right">Otwarte</Th>
            <Th className="text-right">Anulowane</Th>
            <Th>Ocena</Th>
            <Th>Typy zamówień</Th>
            <Th>Ostatnie zamówienie</Th>
          </tr>
        </thead>
        <tbody>
          {contractors.map((c) => (
            <ContractorRow
              key={c.id}
              contractor={c}
              connectionId={connection.id}
              expanded={expanded.has(c.id)}
              onToggle={() => toggle(c.id)}
              canOpenContractor={canOpenContractor}
              canOpenOrder={canOpenOrder}
              canPreviewMap={previewIds.has(c.id)}
            />
          ))}
        </tbody>
      </TableShell>
    </div>
  );
}

function ContractorRow({
  contractor: c,
  connectionId,
  expanded,
  onToggle,
  canOpenContractor,
  canOpenOrder,
  canPreviewMap,
}: {
  contractor: ProjectContractorRow;
  connectionId: string;
  expanded: boolean;
  onToggle: () => void;
  canOpenContractor: boolean;
  canOpenOrder: boolean;
  canPreviewMap: boolean;
}) {
  return (
    <>
      <tr
        onClick={onToggle}
        className="cursor-pointer border-t border-[var(--color-border)] hover:bg-[var(--color-bg-elevated)]"
      >
        <td className="px-3 py-2 text-[var(--color-text-muted)]">
          {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </td>
        <td className="px-3 py-2">
          <div className="flex items-center gap-2">
            {canOpenContractor ? (
              <Link
                to={`/contractors/${c.id}`}
                onClick={(e) => e.stopPropagation()}
                className="font-medium text-[var(--color-primary)] hover:underline"
              >
                {c.name}
              </Link>
            ) : (
              <span className="font-medium text-[var(--color-text-primary)]">{c.name}</span>
            )}
            {c.short_name && (
              <span className="text-[var(--color-text-muted)]">({c.short_name})</span>
            )}
            {!c.is_active && (
              <span className="rounded bg-[var(--color-bg-elevated)] px-1.5 py-0.5 font-mono text-[0.6rem] uppercase tracking-wider text-[var(--color-text-muted)]">
                nieaktywny
              </span>
            )}
            {canPreviewMap && (
              <Link
                to={`/power-projects/contractor-preview/${c.id}/${connectionId}`}
                onClick={(e) => e.stopPropagation()}
                className="ml-1 inline-flex items-center gap-1 border border-[var(--color-border)] px-1.5 py-0.5 font-mono text-[0.6rem] uppercase tracking-wider text-[var(--color-text-muted)] hover:border-[var(--color-primary)] hover:text-[var(--color-primary)]"
                title={`Zobacz dokładnie to, co ${c.name} widzi na mapie tego projektu (te same dane, ten sam eksport)`}
              >
                <Eye size={11} /> Oczami podwykonawcy
              </Link>
            )}
          </div>
        </td>
        <td className="px-3 py-2 text-right tabular-nums">{c.orders_count}</td>
        <td className="px-3 py-2 text-right tabular-nums">
          {c.open_orders_count > 0 ? (
            <span className="font-semibold text-[var(--color-warn,#b45309)]">
              {c.open_orders_count}
            </span>
          ) : (
            <span className="text-[var(--color-text-muted)]">0</span>
          )}
        </td>
        <td className="px-3 py-2 text-right tabular-nums">
          {c.cancelled_orders_count > 0 ? (
            <span className="text-[var(--color-text-secondary)]">{c.cancelled_orders_count}</span>
          ) : (
            <span className="text-[var(--color-text-muted)]">0</span>
          )}
        </td>
        <td className="px-3 py-2">
          {c.rating != null ? (
            <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
              <Stars value={c.rating} />
              <span className="tabular-nums text-[var(--color-text-secondary)]">
                {c.rating.toFixed(2)} ({c.ratings_count})
              </span>
            </span>
          ) : (
            <span className="text-[var(--color-text-muted)]">—</span>
          )}
        </td>
        <td className="px-3 py-2 text-[var(--color-text-secondary)]">{c.order_types.join(", ")}</td>
        <td className="px-3 py-2 text-[var(--color-text-secondary)]">{fmtDate(c.last_order_at)}</td>
      </tr>
      {expanded && (
        <tr className="border-t border-[var(--color-border-muted)] bg-[var(--color-bg-elevated)]/40">
          <td />
          <td colSpan={7} className="px-3 pb-3">
            <ContractorOrdersPanel
              connectionId={connectionId}
              contractorId={c.id}
              canOpenOrder={canOpenOrder}
            />
          </td>
        </tr>
      )}
    </>
  );
}

function ContractorOrdersPanel({
  connectionId,
  contractorId,
  canOpenOrder,
}: {
  connectionId: string;
  contractorId: string;
  canOpenOrder: boolean;
}) {
  // Lazy — pobierane dopiero po rozwinięciu wiersza (enabled=true przy zamontowaniu).
  const { data: orders, isLoading } = useContractorOrders(connectionId, contractorId, true);

  if (isLoading)
    return (
      <div className="py-4 flex justify-center">
        <Spinner />
      </div>
    );

  if (!orders || orders.length === 0)
    return (
      <p className="py-2 text-[11px] font-mono text-[var(--color-text-muted)]">
        Brak zamówień do wyświetlenia.
      </p>
    );

  return (
    <div className="space-y-2 py-1">
      {orders.map((o) => (
        <ContractorOrderCard key={o.id} order={o} canOpenOrder={canOpenOrder} />
      ))}
    </div>
  );
}

function ContractorOrderCard({
  order,
  canOpenOrder,
}: {
  order: ContractorOrderRow;
  canOpenOrder: boolean;
}) {
  // „Dane zapotrzebowania" — po kliknięciu odsłaniamy read-only widok w dwóch kolumnach
  // wraz z załącznikami zapotrzebowania.
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-[var(--color-border)] bg-[var(--color-bg-surface)]">
      <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-2">
        <div className="flex min-w-0 items-center gap-2">
          {canOpenOrder ? (
            <Link
              to={`/orders/${order.id}`}
              className="inline-flex items-center gap-1 font-mono text-xs font-semibold text-[var(--color-primary)] hover:underline"
            >
              {order.number}
              <ExternalLink size={12} />
            </Link>
          ) : (
            <span className="font-mono text-xs font-semibold text-[var(--color-text-primary)]">
              {order.number}
            </span>
          )}
          <span className="text-[11px] text-[var(--color-text-muted)]">
            {order.order_type_display}
          </span>
          <span className="rounded bg-[var(--color-bg-elevated)] px-1.5 py-0.5 text-[10px] font-mono uppercase tracking-wide text-[var(--color-text-secondary)]">
            {order.status_display}
          </span>
        </div>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="inline-flex items-center gap-1 text-[11px] font-mono text-[var(--color-primary)] hover:underline"
        >
          {open ? <ChevronDown size={13} /> : <ChevronRight size={13} />} Dane zapotrzebowania
        </button>
      </div>

      {open && (
        <div className="space-y-3 border-t border-[var(--color-border-muted)] px-3 py-3">
          <div className="grid grid-cols-2 gap-3">
            <Info label="Typ zamówienia" value={order.order_type_display} />
            <Info label="Status" value={order.status_display} />
            <Info label="Priorytet" value={order.priority_display} />
            <Info label="Termin realizacji" value={fmtDate(order.deadline)} />
            <Info label="Zgłaszający / zamawiający" value={order.reporter_name ?? "—"} />
            <Info label="Zatwierdzono" value={fmtDate(order.approved_at)} />
          </div>

          {order.description && (
            <div>
              <p className="section-label mb-1">Opis</p>
              {order.description.trimStart().startsWith("<") ? (
                <div
                  className="rich-text-content text-sm text-[var(--color-text-secondary)]"
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(order.description, {
                      ALLOWED_TAGS: ["p", "br", "strong", "em", "u", "ul", "ol", "li", "h1", "h2", "h3"],
                      ALLOWED_ATTR: ["start"],
                    }),
                  }}
                />
              ) : (
                <p className="whitespace-pre-wrap text-sm text-[var(--color-text-secondary)]">
                  {order.description}
                </p>
              )}
            </div>
          )}

          <div>
            <p className="section-label mb-1 flex items-center gap-1">
              <Paperclip size={12} /> Załączniki ({order.files.length})
            </p>
            <AttachmentList files={order.files} />
          </div>
        </div>
      )}
    </div>
  );
}

function Info({ label, value }: { label: string; value: string }) {
  return (
    <div className="border border-[var(--color-border-muted)] bg-[var(--color-bg-elevated)]/40 px-3 py-2">
      <p className="text-[10px] font-mono uppercase tracking-wide text-[var(--color-text-muted)]">
        {label}
      </p>
      <p className="mt-0.5 text-sm text-[var(--color-text-primary)]">{value}</p>
    </div>
  );
}
